import * as Sentry from '@sentry/react'

const SENSITIVE_KEY_PATTERN = /token|key|secret|password|mfa/i

/**
 * Scrub sensitive fields from event data before sending to Sentry.
 * Matches keys containing: token, key, secret, password, mfa (case-insensitive).
 */
function scrubSensitiveData(data: Record<string, unknown>): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(data)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      scrubbed[key] = '[REDACTED]'
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      scrubbed[key] = scrubSensitiveData(value as Record<string, unknown>)
    } else {
      scrubbed[key] = value
    }
  }
  return scrubbed
}

/**
 * Initialize Sentry error tracking for dextoro-admin.
 * Must be called before createRoot() in main.tsx.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    console.warn('[sentry] VITE_SENTRY_DSN not set, Sentry disabled')
    return
  }

  const environment = import.meta.env.VITE_APP_ENV || 'development'

  Sentry.init({
    dsn,
    environment,
    enabled: !!dsn,

    // Performance monitoring. 0.05 is the workspace standard after the April
    // 2026 budget remediation; admin gets very low traffic so keeping it at
    // 0.2 was pure overhead.
    tracesSampleRate: environment === 'production' ? 0.05 : 1.0,

    // Session replay (off by default, enable if needed)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: environment === 'production' ? 0.1 : 0,

    // Ignore common transient network errors
    ignoreErrors: [
      'ECONNRESET',
      'ECONNREFUSED',
      'ETIMEDOUT',
      'fetch failed',
      'Failed to fetch',
      'Load failed',
      'NetworkError',
      'Network request failed',
      'AbortError',
      'TypeError: cancelled',
      'TypeError: Cancelled',
      'ResizeObserver loop',
      'Non-Error promise rejection captured',
    ],

    beforeSend(event) {
      // Scrub sensitive data from event extras
      if (event.extra) {
        event.extra = scrubSensitiveData(event.extra as Record<string, unknown>)
      }

      // Scrub sensitive data from breadcrumb data
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data) {
            breadcrumb.data = scrubSensitiveData(breadcrumb.data as Record<string, unknown>)
          }
          return breadcrumb
        })
      }

      // Scrub sensitive data from contexts
      if (event.contexts) {
        for (const [contextKey, contextValue] of Object.entries(event.contexts)) {
          if (contextValue && typeof contextValue === 'object') {
            event.contexts[contextKey] = scrubSensitiveData(
              contextValue as Record<string, unknown>
            )
          }
        }
      }

      return event
    },

    // Tag every event with the environment
    initialScope: {
      tags: {
        app: 'dextoro-admin',
        environment,
      },
    },
  })
}

/**
 * Capture an error with optional structured context.
 * Sensitive fields in context are automatically scrubbed.
 */
export function captureError(error: Error, context?: Record<string, unknown>): void {
  Sentry.withScope((scope) => {
    if (context) {
      const safeContext = scrubSensitiveData(context)
      for (const [key, value] of Object.entries(safeContext)) {
        scope.setExtra(key, value)
      }
    }
    Sentry.captureException(error)
  })
}

/**
 * Capture a message (non-exception) event in Sentry.
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
  Sentry.captureMessage(message, level)
}

/**
 * Add a breadcrumb for debugging context.
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    data: data ? scrubSensitiveData(data) : undefined,
    level: 'info',
  })
}

/**
 * Set the current user context in Sentry using a wallet address.
 * Never sends email or PII.
 */
export function setUser(walletAddress: string): void {
  Sentry.setUser({
    id: walletAddress,
  })
}

/**
 * Clear user context from Sentry (e.g., on sign out).
 */
export function clearUser(): void {
  Sentry.setUser(null)
}
